import { useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { loadExotelCrmWebSDK } from "../../utils/exotelWebrtcSdk";
import "./ExotelInAppCallSheet.css";

function formatTimer(totalSec) {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function initialFor(name) {
  const t = String(name || "C").trim();
  return t ? t.slice(0, 1).toUpperCase() : "C";
}

function classifyEvent(payload) {
  const raw = String(
    payload?.CallStatus ||
      payload?.callStatus ||
      payload?.Status ||
      payload?.status ||
      payload?.event ||
      payload?.Event ||
      payload ||
      ""
  ).toLowerCase();
  if (/^success$/.test(raw)) return "ringing";
  if (/register|registered|ready/.test(raw) && !/unreg/.test(raw)) return "registered";
  if (/ring/.test(raw)) return "ringing";
  if (/connect|answer|in.?progress|established|active|bridged/.test(raw)) return "connected";
  if (/mute/.test(raw)) return "muted";
  if (/fail|reject|busy|error|cancelled/.test(raw)) return "failed";
  if (/end|hang|disconnect|complete/.test(raw)) return "ended";
  return "";
}

export default function ExotelInAppCallSheet({
  sessionId,
  accessToken,
  appUserId,
  peerName = "Caregiver",
  peerPhotoUrl = "",
  onClose,
}) {
  const [phase, setPhase] = useState("connecting");
  const [error, setError] = useState("");
  const [muted, setMuted] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const phoneRef = useRef(null);
  const startedRef = useRef(false);
  const connectedAtRef = useRef(0);

  useEffect(() => {
    if (phase !== "connected") return undefined;
    connectedAtRef.current = Date.now();
    const timer = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - connectedAtRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;
    let cancelled = false;

    async function boot() {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
          stream.getTracks().forEach((track) => track.stop());
        });
      } catch {
        if (!cancelled) {
          setError("Allow microphone access to talk in the app.");
          setPhase("failed");
        }
        return;
      }

      let phone;
      try {
        const ExotelCRMWebSDK = await loadExotelCrmWebSDK();
        if (!ExotelCRMWebSDK) throw new Error("sdk_unavailable");
        const sdk = new ExotelCRMWebSDK(accessToken, appUserId, true);
        let resolveSip;
        const sipRegistered = new Promise((resolve) => {
          resolveSip = resolve;
        });
        const handleCall = (event, details) => {
          if (cancelled) return;
          const next = classifyEvent(event) || classifyEvent(details);
          if (next === "ringing") setPhase("ringing");
          if (next === "connected") setPhase("connected");
          if (next === "failed") {
            setError("They could not be reached. Try again in a moment.");
            setPhase("failed");
          }
          if (next === "ended") setPhase("ended");
        };
        const handleRegister = (event) => {
          const raw = String(event || "").toLowerCase();
          const next = classifyEvent(event);
          if (raw === "connected" || next === "registered") {
            resolveSip(true);
            return;
          }
          if (cancelled) return;
          if (raw === "failed_to_start" || raw === "transport_error" || next === "failed") {
            resolveSip(false);
            setError("Could not start in-app calling. Please try again.");
            setPhase("failed");
          }
        };
        phone = await sdk.Initialize(handleCall, handleRegister, handleCall);
        phoneRef.current = phone;
        if (!phone) {
          throw new Error("sip_unavailable");
        }
        if (typeof phone.RegisterDevice === "function") {
          try {
            phone.RegisterDevice();
          } catch {
            /* autoConnectVoip already registers */
          }
        }
        const sipReady = await Promise.race([
          sipRegistered,
          new Promise((resolve) => window.setTimeout(() => resolve("timeout"), 15000)),
        ]);
        if (cancelled) return;
        if (sipReady === false) {
          setError("Could not start in-app calling. Please try again.");
          setPhase("failed");
          return;
        }
        if (sipReady === "timeout") {
          setError("Stay in this screen and allow the microphone. We could not start in-app calling.");
          setPhase("failed");
          return;
        }
        setPhase("ringing");
        if (!sessionId) throw new Error("sip_unavailable");
        try {
          await api.bridgeMessengerCall(sessionId);
        } catch (bridgeErr) {
          if (!cancelled) {
            setError(
              String(bridgeErr?.message || "").replace(/\+?\d[\d\s-]{8,}\d/g, "the other person") ||
                "We could not start the in-app call. Please try again in a moment."
            );
            setPhase("failed");
          }
          return;
        }
      } catch (err) {
        if (!cancelled) {
          const code = String(err?.message || "");
          if (code === "sip_unavailable") {
            setError("In-app calling is not enabled on this Exotel account yet. Ask Exotel to turn on IP-PSTN.");
          } else {
            setError("In-app calling could not start in this browser. Use Chrome or Edge on HTTPS.");
          }
          setPhase("failed");
        }
      }
    }

    boot();
    return () => {
      cancelled = true;
      try {
        phoneRef.current?.HangupCall?.();
        phoneRef.current?.UnRegisterDevice?.();
      } catch {
        /* ignore */
      }
    };
  }, [accessToken, appUserId, sessionId]);

  async function hangupAndClose() {
    try {
      phoneRef.current?.HangupCall?.();
      phoneRef.current?.UnRegisterDevice?.();
    } catch {
      /* ignore */
    }
    if (sessionId) {
      try {
        await api.endMessengerCall(sessionId);
      } catch {
        /* session may already be ended */
      }
    }
    onClose?.();
  }

  function toggleMute() {
    try {
      phoneRef.current?.ToggleMute?.();
      setMuted((v) => !v);
    } catch {
      /* ignore */
    }
  }

  const statusText =
    phase === "connecting"
      ? "Connecting…"
      : phase === "ringing"
        ? "Calling caregiver…"
        : phase === "connected"
          ? "On call"
          : phase === "ended"
            ? "Call ended"
            : "Could not connect";

  return (
    <div className="exotel-inapp" role="dialog" aria-modal="true" aria-labelledby="exotel-inapp-title">
      <div className="exotel-inapp__card">
        <div className="exotel-inapp__avatar" aria-hidden>
          {peerPhotoUrl ? <img src={peerPhotoUrl} alt="" /> : initialFor(peerName)}
        </div>
        <h2 id="exotel-inapp-title" className="exotel-inapp__name">
          {peerName}
        </h2>
        <p className="exotel-inapp__status">{statusText}</p>
        {phase === "connected" ? <p className="exotel-inapp__timer">{formatTimer(elapsedSec)}</p> : null}
        {error ? <p className="exotel-inapp__error">{error}</p> : null}
        {phase === "ringing" || phase === "connecting" ? (
          <p className="exotel-inapp__hint">Numbers stay private. They will see the NemaCare number, not yours.</p>
        ) : null}
        <div className="exotel-inapp__actions">
          {phase === "connected" ? (
            <button type="button" className="exotel-inapp__btn exotel-inapp__btn--mute" onClick={toggleMute}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                {muted ? (
                  <path d="M5 5l14 14M12 4a3 3 0 00-3 3v5m10 0v-2a7 7 0 00-7-7" />
                ) : (
                  <path d="M12 3a3 3 0 00-3 3v6a3 3 0 006 0V6a3 3 0 00-3-3zM19 11a7 7 0 01-14 0M12 18v3" />
                )}
              </svg>
              {muted ? "Unmute" : "Mute"}
            </button>
          ) : null}
          <button type="button" className="exotel-inapp__btn exotel-inapp__btn--hangup" onClick={hangupAndClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.86 19.86 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.86 19.86 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.12.96.37 1.9.73 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.36 1.85.61 2.81.73A2 2 0 0122 16.92z" />
            </svg>
            {phase === "failed" || phase === "ended" ? "Close" : "End call"}
          </button>
        </div>
      </div>
    </div>
  );
}
